import React, { FC, useContext, useLayoutEffect } from 'react'

import { useFormContext } from 'react-hook-form'

import { AppContext } from 'Const'
import { useCustomer } from 'Hooks/checkout'
import { PaymentMethods } from 'Types'

import { usePayment } from '../Provider'

import PaymentOption from './PaymentOption'
import SpreedlyPaymentFields from './SpreedlyPaymentFields'
import SquarePaymentFields from './SquarePaymentFields'
import StripePaymentFields from './StripePaymentFields'

interface Props {
  isActive: boolean
}

const PaymentOptions: FC<Props> = ({ isActive }) => {
  const { shop } = useContext(AppContext)
  const { gateway } = usePayment()
  const { applePay, googlePay, field } = gateway
  const { register, unregister, setValue, watch } = useFormContext()

  const customer = useCustomer()
  const cards = gateway.isSpreedly ? customer?.spreedlyCards : gateway.isSquare ? customer?.squareCards : []
  const firstCard = cards?.[0]

  const paymentMethod = watch(`${field}.paymentMethod`)
  const sourceId = watch(`${field}.sourceId`)
  const cardSelected = cards?.some((card) => card.id == sourceId)
  const isPlainCardOption = paymentMethod == PaymentMethods.CARD && !cardSelected

  useLayoutEffect(() => {
    // TODO: add `value` to register after migrate to react-hook-form 7.18
    const fields = [`${field}.payByEnteredCard`, `${field}.paymentMethod`]
    fields.forEach((field) => register(field))
    fields.push(`${field}.sourceId`)
    register(`${field}.sourceId`, { required: 'Please check card information' })
    return () => fields.forEach((field) => unregister(field))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field])

  useLayoutEffect(() => {
    let payByEnteredCard = shop.isPaymentRequired
    if (applePay) {
      setValue(`${field}.paymentMethod`, PaymentMethods.APPLE_PAY)
    } else {
      setValue(`${field}.paymentMethod`, PaymentMethods.CARD)
      if (firstCard) {
        setValue(`${field}.sourceId`, firstCard.id)
        payByEnteredCard = false
      }
    }
    setValue(`${field}.payByEnteredCard`, payByEnteredCard)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, firstCard, applePay])

  useLayoutEffect(
    () => {
      if (!shop.isPaymentRequired) setValue(`${field}.payByEnteredCard`, shop.isPaymentRequired)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [field, shop.isPaymentRequired],
  )

  return (
    <div>
      {applePay ? (
        <PaymentOption
          key={PaymentMethods.APPLE_PAY}
          paymentMethod={PaymentMethods.APPLE_PAY}
          selected={paymentMethod == PaymentMethods.APPLE_PAY}
        />
      ) : null}
      {googlePay ? (
        <PaymentOption
          key={PaymentMethods.GOOGLE_PAY}
          paymentMethod={PaymentMethods.GOOGLE_PAY}
          selected={paymentMethod == PaymentMethods.GOOGLE_PAY}
        />
      ) : null}
      {cards?.map((card) => (
        <PaymentOption
          key={card.id}
          card={card}
          paymentMethod={PaymentMethods.CARD}
          selected={paymentMethod == PaymentMethods.CARD && sourceId == card.id}
        />
      ))}
      <PaymentOption key={PaymentMethods.CARD} paymentMethod={PaymentMethods.CARD} selected={isPlainCardOption}>
        {gateway.isStripe ? (
          <StripePaymentFields />
        ) : gateway.isSquare ? (
          <SquarePaymentFields isActive={isActive && isPlainCardOption} />
        ) : gateway.isSpreedly ? (
          <SpreedlyPaymentFields />
        ) : null}
      </PaymentOption>
    </div>
  )
}

export default PaymentOptions
